import { createApi } from "@reduxjs/toolkit/query/react"
import { baseQueryWithAuth } from "~/ui-rtk/utils/base-query"

export type IngestionStatus =
  | "PENDING"
  | "VALIDATING"
  | "VALIDATION_FAILED"
  | "PROCESSING"
  | "COMPLETED"
  | "FAILED"

export interface ValidationError {
  code: string
  message: string
  column?: string
  row?: number
}

export interface ProcessingError {
  code: string
  message: string
  details?: string
}

/**
 * Auto-fix report produced by the csv_ingestion Lambda (APP-375/APP-376),
 * persisted to `IngestionHistory.metadata.normalization`. Mirrors the Python
 * `NormalizationReport.to_metadata()`.
 */
export interface NormalizationReport {
  file_type: string
  // Worksheet read from an .xlsx when the report pins one (`sheetName`, APP-563).
  sheet_name?: string | null
  encoding?: string | null
  encoding_changed: boolean
  delimiter?: string | null
  delimiter_changed: boolean
  header_rows_skipped: number
  // Collection fields are optional: a report may omit them, and the UI accesses
  // them defensively (?? [] / ?? {}).
  renamed_columns?: Record<string, string>
  dropped_columns?: string[]
  missing_columns?: string[]
  blanks_filled_columns?: string[]
  high_null_columns?: { column: string; null_rate: number }[]
  row_count: number
}

export interface IngestionMetadata {
  normalization?: NormalizationReport
  parquet_key?: string
  datalake_key?: string
  row_count?: number
  file_size?: number
  columnMapping?: Record<string, string>
  matchedTable?: string
  [key: string]: unknown
}

export interface IngestionHistory {
  id: string
  companyId: string
  companyConnectorId?: string
  connectorId: string
  reportType?: string | null
  deliveryMethod: string
  fileName: string
  fileSize: number
  status: IngestionStatus
  validationErrors?: ValidationError[]
  processingErrors?: ProcessingError[]
  metadata?: IngestionMetadata
  createdAt: string
  updatedAt: string
  processedAt?: string
}

export interface IngestionHistoryQuery {
  companyConnectorId?: string
  status?: IngestionStatus
  deliveryMethod?: string
  reportType?: string
  limit?: number
  offset?: number
}

export interface IngestionStatistics {
  total: number
  byStatus: Record<IngestionStatus, number>
  byDeliveryMethod: Record<string, number>
}

export interface UploadCSVResponse {
  ingestionHistoryId: string
  status: IngestionStatus
  message: string
}

export interface UploadCSVRequest {
  file: File
  companyConnectorId: string
  /** Per-report key under Connector.metadata.ingestionConfig.reports */
  reportType: string
}

export interface PresignUploadRequest {
  companyConnectorId: string
  reportType: string
  fileName: string
  size: number
  contentType?: string
}

/**
 * Presigned S3 PUT for a large upload (APP-537). `requiredHeaders` are signed
 * into `uploadUrl`: the PUT must send them verbatim or S3 rejects it.
 */
export interface PresignUploadResponse {
  uploadUrl: string
  key: string
  requiredHeaders: Record<string, string>
  expiresIn: number
  ingestionHistoryId: string
}

export const ingestionApi = createApi({
  reducerPath: "ingestionApi",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["IngestionHistory"],
  endpoints: builder => ({
    getIngestionHistoryList: builder.query<
      IngestionHistory[],
      IngestionHistoryQuery
    >({
      query: params => ({
        url: "/ingestion-history",
        params,
      }),
      providesTags: ["IngestionHistory"],
    }),

    getIngestionHistoryById: builder.query<IngestionHistory, string>({
      query: id => ({
        url: `/ingestion-history/${id}`,
      }),
      providesTags: (_result: unknown, _error: unknown, id: string) => [
        { type: "IngestionHistory", id },
      ],
    }),

    getIngestionHistoryByConnector: builder.query<
      IngestionHistory[],
      {
        companyConnectorId: string
        limit?: number
        offset?: number
        reportType?: string
      }
    >({
      query: ({ companyConnectorId, limit, offset, reportType }) => ({
        url: `/ingestion-history`,
        params: {
          companyConnectorId,
          limit,
          offset,
          ...(reportType ? { reportType } : {}),
        },
      }),
      providesTags: ["IngestionHistory"],
    }),

    getIngestionStatistics: builder.query<IngestionStatistics, void>({
      query: () => ({
        url: "/ingestion-history/statistics",
      }),
      providesTags: ["IngestionHistory"],
    }),

    uploadCSV: builder.mutation<UploadCSVResponse, UploadCSVRequest>({
      query: ({ file, companyConnectorId, reportType }) => {
        const formData = new FormData()
        formData.append("file", file)
        formData.append("reportType", reportType)

        return {
          url: `/company-connector/${companyConnectorId}/ingest-csv`,
          method: "POST",
          body: formData,
        }
      },
      invalidatesTags: ["IngestionHistory"],
    }),

    // Creates the PENDING history row and signs the PUT; the bytes themselves
    // go browser → S3 via uploadToPresignedUrl, never through this API.
    presignUpload: builder.mutation<
      PresignUploadResponse,
      PresignUploadRequest
    >({
      query: ({ companyConnectorId, ...body }) => ({
        url: `/company-connector/${companyConnectorId}/ingest-csv/presign`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["IngestionHistory"],
    }),
  }),
})

export const {
  useGetIngestionHistoryListQuery,
  useGetIngestionHistoryByIdQuery,
  useGetIngestionHistoryByConnectorQuery,
  useGetIngestionStatisticsQuery,
  useUploadCSVMutation,
  usePresignUploadMutation,
} = ingestionApi
